import type { InfrastructurePlanSection } from "../i18n/architecturePlanCopy";
import type { Locale } from "../i18n/locale";
import { numberLocaleFor } from "../i18n/locale";
import type { AwsRegionId } from "./awsRegion";
import { LINE_ITEM_COLORS } from "./chartColors";
import { buildCostMixDonutSlices, COST_MIX_DONUT } from "./costMixDonut";
import type { CostEstimate, OptimizationResult, ScalingPoint, Workload } from "./types";
import type { SensitivityDeltas } from "./sensitivity";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export type HtmlReportLabels = {
  mix: string;
  scaling: string;
  sensitivity: string;
  breakdown: string;
  workload: string;
  assumptions: string;
  risks: string;
  suggestions: string;
  architecture: string;
  footer: string;
  printHint: string;
  scalingCaption: string;
  sensitivityDau: string;
  sensitivityReq: string;
  sensitivityAiShare: string;
  sensitivityTokens: string;
  total: string;
  mixColItem: string;
  mixColPct: string;
  mixColUsd: string;
  planBlueprint: string;
  contactLead: string;
  legalHeading: string;
  legalBody: string;
};

function svgCostMix(estimate: CostEstimate, w: number, h: number): string {
  const total = estimate.totalMonthlyUsd;
  const innerW = w - 24;
  let x = 12;
  const y = 28;
  const barH = 22;
  const rects: string[] = [];
  if (total <= 0) {
    rects.push(
      `<rect x="${12}" y="${y}" width="${innerW}" height="${barH}" rx="4" fill="#333" />`,
    );
  } else {
    for (const row of estimate.lineItems) {
      const frac = row.monthlyUsd / total;
      const segW = Math.max(2, innerW * frac);
      const fill = LINE_ITEM_COLORS[row.id] ?? "#666";
      rects.push(
        `<rect x="${x.toFixed(1)}" y="${y}" width="${segW.toFixed(1)}" height="${barH}" rx="3" fill="${fill}" />`,
      );
      x += segW;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="mix">
    ${rects.join("\n    ")}
  </svg>`;
}

function svgCostMixDonut(estimate: CostEstimate): string {
  const slices = buildCostMixDonutSlices(estimate);
  const { vb, cx, cy, rOuter, rInner } = COST_MIX_DONUT;
  const holeFill = "#1c1d24";
  const sliceStroke = holeFill;
  if (slices.length === 0) {
    const rm = (rOuter + rInner) / 2;
    const sw = rOuter - rInner;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb} ${vb}" role="img" aria-hidden="true"><circle cx="${cx}" cy="${cy}" r="${rm}" fill="none" stroke="#444" stroke-width="${sw}"/></svg>`;
  }
  const parts: string[] = [];
  for (const s of slices) {
    if (s.kind === "ring") {
      parts.push(
        `<circle cx="${cx}" cy="${cy}" r="${rOuter}" fill="${esc(s.fill)}" stroke="${sliceStroke}" stroke-width="0.9"/>`,
      );
      parts.push(`<circle cx="${cx}" cy="${cy}" r="${rInner}" fill="${holeFill}"/>`);
    } else {
      parts.push(
        `<path fill="${esc(s.fill)}" stroke="${sliceStroke}" stroke-width="0.85" paint-order="stroke fill" d="${s.path}"><title>${esc(s.row.label)}</title></path>`,
      );
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb} ${vb}" role="img">${parts.join("")}</svg>`;
}

function svgScaling(
  points: ScalingPoint[],
  fmt: (n: number) => string,
  labels: Pick<HtmlReportLabels, "scalingCaption">,
  w: number,
  h: number,
): string {
  const pad = { l: 48, r: 16, t: 20, b: 52 };
  const cw = w - pad.l - pad.r;
  const ch = h - pad.t - pad.b;
  const maxUsd = Math.max(...points.map((p) => p.totalMonthlyUsd), 1);
  const n = points.length;
  const gap = 8;
  const barW = (cw - gap * (n - 1)) / n;
  const bars = points
    .map((p, i) => {
      const bx = pad.l + i * (barW + gap);
      const bh = (p.totalMonthlyUsd / maxUsd) * ch;
      const by = pad.t + ch - bh;
      return `<rect x="${bx.toFixed(1)}" y="${by.toFixed(1)}" width="${barW.toFixed(1)}" height="${Math.max(bh, 1).toFixed(1)}" rx="4" fill="#c8e66d" fill-opacity="0.85"/>
      <text x="${(bx + barW / 2).toFixed(1)}" y="${(h - 18).toFixed(0)}" text-anchor="middle" fill="#aaa" font-size="11" font-family="system-ui,sans-serif">${esc(String(p.usersDaily))}</text>
      <text x="${(bx + barW / 2).toFixed(1)}" y="${(by - 6).toFixed(0)}" text-anchor="middle" fill="#e8e8e8" font-size="10" font-family="system-ui,sans-serif">${esc(fmt(p.totalMonthlyUsd))}</text>`;
    })
    .join("\n");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="scaling">
    <text x="${pad.l}" y="14" fill="#ccc" font-size="12" font-family="system-ui,sans-serif">${esc(labels.scalingCaption)}</text>
    <line x1="${pad.l}" y1="${pad.t + ch}" x2="${w - pad.r}" y2="${pad.t + ch}" stroke="#444" stroke-width="1"/>
    ${bars}
  </svg>`;
}

function svgSensitivity(
  deltas: SensitivityDeltas,
  fmtDelta: (n: number) => string,
  labels: Pick<
    HtmlReportLabels,
    "sensitivityDau" | "sensitivityReq" | "sensitivityAiShare" | "sensitivityTokens"
  >,
  w: number,
  h: number,
): string {
  const padL = 14;
  const padR = 14;
  const labelW = 168;
  const barLeft = padL + labelW;
  const valueX = w - padR;
  const rowH = 36;
  const maxV = Math.max(
    Math.abs(deltas.dauPlus10Usd),
    Math.abs(deltas.requestsPlus10Usd),
    Math.abs(deltas.aiSharePlus10Usd),
    Math.abs(deltas.tokensPlus10Usd),
    1,
  );
  const barMaxW = valueX - barLeft - 72;
  const rows: [string, number][] = [
    [labels.sensitivityDau, deltas.dauPlus10Usd],
    [labels.sensitivityReq, deltas.requestsPlus10Usd],
    [labels.sensitivityAiShare, deltas.aiSharePlus10Usd],
    [labels.sensitivityTokens, deltas.tokensPlus10Usd],
  ];
  const blocks = rows
    .map(([lab, v], i) => {
      const y = 14 + i * (rowH + 10);
      const bw = barMaxW * (Math.abs(v) / maxV);
      const fill = v >= 0 ? "#7eb87e" : "#c97a7a";
      return `<text x="${padL}" y="${y + 22}" fill="#ccc" font-size="12" font-family="system-ui,sans-serif">${esc(lab)}</text>
      <rect x="${barLeft}" y="${y + 6}" width="${Math.max(bw, 2).toFixed(1)}" height="18" rx="3" fill="${fill}" fill-opacity="0.9"/>
      <text x="${valueX}" y="${y + 20}" text-anchor="end" fill="#eee" font-size="11" font-family="ui-monospace,monospace">${esc(fmtDelta(v))}</text>`;
    })
    .join("\n");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="sensitivity">
    ${blocks}
  </svg>`;
}

function mixLegendRows(estimate: CostEstimate, locale: Locale): string {
  const total = estimate.totalMonthlyUsd;
  const nf = numberLocaleFor(locale);
  return estimate.lineItems
    .map((row) => {
      const pct =
        total > 0
          ? ((row.monthlyUsd / total) * 100).toLocaleString(nf, {
              maximumFractionDigits: 1,
            })
          : "—";
      const c = LINE_ITEM_COLORS[row.id] ?? "#666";
      return `<tr><td><span class="sw" style="background:${c}"></span></td><td>${esc(row.label)}</td><td class="num">${esc(pct)}%</td><td class="num">${esc(
        row.monthlyUsd.toLocaleString(nf, {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        }),
      )}</td></tr>`;
    })
    .join("\n");
}

export function buildHtmlReport(opts: {
  locale: Locale;
  region: AwsRegionId;
  workload: Workload;
  estimate: CostEstimate;
  optimization: OptimizationResult;
  title: string;
  scaling: ScalingPoint[];
  sensitivity: SensitivityDeltas;
  plan: InfrastructurePlanSection[];
  labels: HtmlReportLabels;
  contactEmail: string;
  appendixHtml?: string;
  /** Напр. банер read-only знімка. */
  bodyTopHtml?: string;
}): string {
  const {
    locale,
    region,
    workload,
    estimate,
    optimization,
    title,
    scaling,
    sensitivity,
    plan,
    labels,
    contactEmail,
    appendixHtml,
    bodyTopHtml,
  } = opts;
  const nf = numberLocaleFor(locale);
  const lang = locale === "uk" ? "uk" : locale === "es" ? "es" : "en";
  const money = (n: number) =>
    n.toLocaleString(nf, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  const moneyDelta = (n: number) =>
    (n >= 0 ? "+" : "") +
    n.toLocaleString(nf, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  const workloadJson = esc(
    JSON.stringify(
      {
        users_daily: workload.usersDaily,
        requests_per_user: workload.requestsPerUser,
        features: workload.enabledFeatures,
        ai_request_share: workload.aiRequestShare,
        avg_prompt_tokens: workload.avgPromptTokens,
        avg_completion_tokens: workload.avgCompletionTokens,
        storage_kb_per_user: workload.storageKbPerUser,
      },
      null,
      2,
    ),
  );

  const rows = estimate.lineItems
    .map(
      (row) =>
        `<tr><td>${esc(row.label)}</td><td class="num">${esc(money(row.monthlyUsd))}</td></tr>`,
    )
    .join("\n");

  const assumptions = estimate.assumptions.map((a) => `<li>${esc(a)}</li>`).join("");

  const risks =
    optimization.problems.length > 0
      ? `<h2>${esc(labels.risks)}</h2><ul>${optimization.problems.map((p) => `<li><strong>${esc(p.title)}</strong> — ${esc(p.detail)}</li>`).join("")}</ul>`
      : "";

  const suggestions = optimization.suggestions
    .map(
      (s) =>
        `<li><strong>${esc(s.title)}</strong> (${esc(s.effort)}) — ${esc(s.impact)}</li>`,
    )
    .join("");

  const arch =
    optimization.architecture.length > 0
      ? `<h2>${esc(labels.architecture)}</h2><ul>${optimization.architecture.map((a) => `<li><strong>${esc(a.title)}</strong> — ${esc(a.rationale)}</li>`).join("")}</ul>`
      : "";

  const planHtml = plan
    .map(
      (sec) =>
        `<div class="plan-block"><h3>${esc(sec.title)}</h3><ul>${sec.items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul></div>`,
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${esc(title)}</title>
<style>
  :root { --bg:#14151a; --card:#1c1d24; --text:#e8e8ea; --muted:#9a9aa3; --accent:#c8e66d; --border:#2e3038; }
  * { box-sizing: border-box; }
  body { margin:0; font-family: system-ui, -apple-system, Segoe UI, sans-serif; background: var(--bg); color: var(--text); line-height: 1.45; padding: 1.5rem; max-width: 900px; margin-inline: auto; }
  header { margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border); }
  h1 { font-size: 1.5rem; margin: 0 0 0.35rem; letter-spacing: -0.02em; }
  .meta { color: var(--muted); font-size: 0.9rem; margin: 0; }
  .hint { color: var(--muted); font-size: 0.85rem; margin: 1rem 0 0; padding: 0.65rem 0.85rem; background: var(--card); border-radius: 8px; border: 1px solid var(--border); }
  section { margin-bottom: 2rem; }
  h2 { font-size: 1.05rem; margin: 0 0 0.75rem; color: var(--accent); }
  .chart-card { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 1rem; margin-bottom: 1rem; }
  .chart-card svg { display: block; width: 100%; height: auto; max-height: 220px; }
  .chart-mix-visuals { display: grid; grid-template-columns: minmax(140px, 200px) minmax(0, 1fr); gap: 1rem 1.25rem; align-items: center; }
  .chart-mix-donut-wrap svg { max-height: 200px; }
  @media (max-width: 520px) { .chart-mix-visuals { grid-template-columns: 1fr; justify-items: center; } }
  pre { background: #0d0e12; border: 1px solid var(--border); border-radius: 8px; padding: 1rem; overflow: auto; font-size: 0.8rem; }
  table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  th, td { text-align: left; padding: 0.5rem 0.65rem; border-bottom: 1px solid var(--border); }
  th { color: var(--muted); font-weight: 600; }
  .num { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, monospace; }
  tfoot td { font-weight: 700; border-bottom: none; padding-top: 0.75rem; }
  ul { margin: 0.25rem 0; padding-left: 1.2rem; }
  li { margin: 0.35rem 0; }
  .sw { display: inline-block; width: 10px; height: 10px; border-radius: 2px; vertical-align: middle; margin-right: 6px; }
  footer { margin-top: 2rem; padding-top: 1rem; border-top: 1px solid var(--border); font-size: 0.8rem; color: var(--muted); }
  .legal-disclaimer { font-size: 0.72rem; line-height: 1.5; margin: 0.85rem 0; padding: 0.65rem 0.75rem; background: var(--card); border-radius: 8px; border: 1px solid var(--border); color: var(--muted); }
  .legal-disclaimer strong { color: var(--text); }
  .plan-wrap { display: grid; gap: 0.75rem; }
  .plan-block { background: var(--card); border: 1px solid var(--border); border-radius: 10px; padding: 0.85rem 1rem; }
  .plan-block h3 { font-size: 0.95rem; margin: 0 0 0.45rem; color: var(--accent); }
  .plan-block ul { margin: 0; padding-left: 1.1rem; font-size: 0.85rem; }
  .readonly-share-banner { font-size: 0.85rem; padding: 0.65rem 0.85rem; margin-bottom: 1.25rem; background: #2a2410; border: 1px solid #5a5018; border-radius: 8px; color: #e8dc9a; }
  .audit-appendix { margin-bottom: 2rem; padding: 1rem 1.1rem; background: var(--card); border: 1px solid var(--border); border-radius: 10px; font-size: 0.88rem; }
  .audit-appendix h2 { margin-top: 0; }
  .audit-appendix h3 { font-size: 0.95rem; margin: 1rem 0 0.5rem; color: var(--accent); }
  .audit-appendix pre.notes { white-space: pre-wrap; font-size: 0.8rem; margin: 0.5rem 0 0; }
  .audit-foot { font-size: 0.78rem; color: var(--muted); margin: 1rem 0 0; }
  @media print {
    body { background: #fff; color: #111; padding: 0.5in; }
    :root { --bg:#fff; --card:#f6f6f8; --text:#111; --muted:#444; --border:#ccc; --accent:#3d5c00; }
    .hint { border-color: #ccc; }
    pre { background: #f0f0f2; }
  }
</style>
</head>
<body>
${bodyTopHtml ?? ""}
<header>
  <h1>${esc(title)}</h1>
  <p class="meta">AWS region: <code>${esc(region)}</code> · ${esc(labels.total)}: <strong>${esc(money(estimate.totalMonthlyUsd))}</strong></p>
  <p class="hint">${esc(labels.printHint)}</p>
</header>

<section>
  <h2>${esc(labels.mix)}</h2>
  <div class="chart-card chart-mix-visuals"><div class="chart-mix-donut-wrap">${svgCostMixDonut(estimate)}</div><div class="chart-mix-bar-wrap">${svgCostMix(estimate, 640, 72)}</div></div>
  <table><thead><tr><th></th><th>${esc(labels.mixColItem)}</th><th class="num">${esc(labels.mixColPct)}</th><th class="num">${esc(labels.mixColUsd)}</th></tr></thead><tbody>${mixLegendRows(estimate, locale)}</tbody></table>
</section>

<section>
  <h2>${esc(labels.scaling)}</h2>
  <div class="chart-card">${svgScaling(scaling, money, labels, 640, 200)}</div>
</section>

<section>
  <h2>${esc(labels.sensitivity)}</h2>
  <div class="chart-card">${svgSensitivity(sensitivity, moneyDelta, labels, 640, 200)}</div>
</section>

<section>
  <h2>${esc(labels.breakdown)}</h2>
  <table>
    <thead><tr><th>${esc(labels.mixColItem)}</th><th class="num">${esc(labels.mixColUsd)}</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td>${esc(labels.total)}</td><td class="num">${esc(money(estimate.totalMonthlyUsd))}</td></tr></tfoot>
  </table>
</section>

<section>
  <h2>${esc(labels.planBlueprint)}</h2>
  <div class="plan-wrap">${planHtml}</div>
</section>

<section>
  <h2>${esc(labels.workload)}</h2>
  <pre><code>${workloadJson}</code></pre>
</section>

<section>
  <h2>${esc(labels.assumptions)}</h2>
  <ul>${assumptions}</ul>
</section>

${risks}

<section>
  <h2>${esc(labels.suggestions)}</h2>
  <ul>${suggestions}</ul>
</section>

${arch}

${appendixHtml ?? ""}

<footer>
  <p>${esc(labels.footer)}</p>
  <div class="legal-disclaimer"><strong>${esc(labels.legalHeading)}</strong> ${esc(labels.legalBody)}</div>
  <p>${esc(labels.contactLead)}<a href="mailto:${esc(contactEmail)}">${esc(contactEmail)}</a></p>
</footer>
</body>
</html>`;
}
